'use client';
import { TOAST_MESSAGE_TEST_IDS } from '@susu/custom-made/utils/testIds';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

const ENTER_DURATION_MS = 450;
const VISIBLE_DELAY_MS = 1500;
const EXIT_DURATION_MS = 300;

type ToastMessageProps = {
  message: string;
  isOpen: boolean;
  onClose: () => void;
  icon?: string;
  testId?: string;
};

const ToastMessage = ({
  message,
  isOpen,
  onClose,
  icon = 'checkmark',
  testId = TOAST_MESSAGE_TEST_IDS.CONTAINER,
}: ToastMessageProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isLeaving, setIsLeaving] = useState<boolean>(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    timersRef.current.forEach(clearTimeout);
    setIsLeaving(false);
    setIsMounted(true);

    const leaveTimer = setTimeout(() => setIsLeaving(true), ENTER_DURATION_MS + VISIBLE_DELAY_MS);
    const closeTimer = setTimeout(() => {
      setIsMounted(false);
      setIsLeaving(false);
      onCloseRef.current();
    }, ENTER_DURATION_MS + VISIBLE_DELAY_MS + EXIT_DURATION_MS);

    timersRef.current = [leaveTimer, closeTimer];

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // Restart the lifecycle whenever the toast is (re)opened or the message changes.
  }, [isOpen, message]);

  if (!isMounted) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed top-[calc(var(--webstore-header-height)+12px)] left-1/2 z-[1000] flex items-center gap-8 max-w-[calc(100%-32px)] h-40 px-16 bg-white rounded-[80px] shadow-[0_4px_16px_rgba(0,0,0,0.1)] text-xs font-medium leading-xs',
        isLeaving
          ? 'animate-toast-out'
          : 'animate-toast-in',
      )}
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      {icon && (
        <i className="shrink-0 w-16 h-16 text-base flex items-center justify-center susu-icons" translate="no">
          {icon}
        </i>
      )}
      <span className="whitespace-nowrap overflow-hidden text-ellipsis">{message}</span>
    </div>
  );
};

export default ToastMessage;
